import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, ChevronLeft, ChevronRight, Play, X } from 'lucide-react';
import apiClient from '../lib/apiClient';
import { useHotel } from '../context/HotelContext';

// ── YouTube embed helper ──────────────────────────────────────────────────────
const getYoutubeEmbedUrl = (url) => {
  if (!url) return null;
  const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/);
  return match ? `https://www.youtube.com/embed/${match[1]}?autoplay=1&rel=0` : null;
};

const getYoutubeThumbnail = (url) => {
  if (!url) return null;
  const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/);
  return match ? `https://img.youtube.com/vi/${match[1]}/maxresdefault.jpg` : null;
};

// ── Skeleton loader ───────────────────────────────────────────────────────────
const Skeleton = ({ className }) => (
  <div className={`animate-pulse bg-gray-200 rounded-lg ${className}`} />
);

const ExploreSkeleton = () => (
  <div className="min-h-screen pt-20">
    <div className="h-64 bg-gray-200 animate-pulse" />
    <div className="max-w-6xl mx-auto px-4 py-16 space-y-16">
      <div className="space-y-4 text-center">
        <Skeleton className="h-10 w-64 mx-auto" />
        <Skeleton className="h-4 w-96 mx-auto" />
        <Skeleton className="h-4 w-80 mx-auto" />
      </div>
      <Skeleton className="w-full aspect-video max-w-3xl mx-auto rounded-2xl" />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {[1, 2, 3].map(i => <Skeleton key={i} className="h-64 rounded-2xl" />)}
      </div>
    </div>
  </div>
);

// ── Gallery lightbox ──────────────────────────────────────────────────────────
const Lightbox = ({ images, index, onClose }) => {
  const [current, setCurrent] = useState(index);

  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') setCurrent(c => Math.max(0, c - 1));
      if (e.key === 'ArrowRight') setCurrent(c => Math.min(images.length - 1, c + 1));
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [images.length, onClose]);

  return (
    <div className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center" onClick={onClose}>
      <button
        onClick={onClose}
        className="absolute top-4 right-4 z-10 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
      >
        <X size={24} />
      </button>

      {current > 0 && (
        <button
          onClick={e => { e.stopPropagation(); setCurrent(c => c - 1); }}
          className="absolute left-4 z-10 p-3 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
        >
          <ChevronLeft size={24} />
        </button>
      )}

      <img
        src={images[current].url}
        alt=""
        className="max-h-[85vh] max-w-[90vw] object-contain rounded-lg"
        onClick={e => e.stopPropagation()}
      />

      {current < images.length - 1 && (
        <button
          onClick={e => { e.stopPropagation(); setCurrent(c => c + 1); }}
          className="absolute right-4 z-10 p-3 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
        >
          <ChevronRight size={24} />
        </button>
      )}

      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1.5">
        {images.map((_, i) => (
          <button
            key={i}
            onClick={e => { e.stopPropagation(); setCurrent(i); }}
            className={`w-2 h-2 rounded-full transition-all ${i === current ? 'bg-white scale-125' : 'bg-white/40'}`}
          />
        ))}
      </div>
    </div>
  );
};

// ── Main Explore page ────────────────────────────────────────────────────────
const Explore = () => {
  const { hotel } = useHotel();
  const navigate = useNavigate();
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [videoPlaying, setVideoPlaying] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const videoRef = useRef(null);

  useEffect(() => {
    apiClient.get('/api/Explore/GetContent')
      .then(data => {
        // Only redirect if the feature is disabled at the SaaS level
        if (data?.isExploreEnabled === false) {
          navigate('/', { replace: true });
          return;
        }
        setContent(data || {});
      })
      .catch(() => {
        // API error — show empty state, don't redirect
        setContent({});
      })
      .finally(() => setLoading(false));
  }, [navigate]);

  // Scroll to top on mount
  useEffect(() => { window.scrollTo(0, 0); }, []);

  if (loading) return <ExploreSkeleton />;
  if (!content) return null;

  const embedUrl = getYoutubeEmbedUrl(content.youtubeVideoUrl);
  const thumbUrl = getYoutubeThumbnail(content.youtubeVideoUrl);
  const primaryColor = hotel?.primaryColor || '#2563eb';
  const hasContent = content.pageTitle || content.activities?.length > 0 || content.galleryImages?.length > 0;

  // Page not published yet — show a "coming soon" hero using the same cover image
  if (!content.isActive && !hasContent) {
    return (
      <div className="min-h-screen bg-white">
        <section
          className="relative flex items-center justify-center bg-cover bg-center bg-no-repeat"
          style={{
            minHeight: '75vh',
            backgroundImage: hotel?.bannerUrl
              ? `url(${hotel.bannerUrl})`
              : `linear-gradient(135deg, #0f172a 0%, #1e293b 100%)`,
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/70" />
          <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-white to-transparent" />
          <div className="container relative z-10 text-center pt-20 pb-16">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-medium mb-6 bg-white/15 backdrop-blur-sm border border-white/20 text-white/90">
              <MapPin size={14} />
              {hotel?.hotelName}
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-white font-playfair mb-4 drop-shadow-lg">Explore Us</h1>
            <p className="text-xl text-white/80 font-light drop-shadow">Discover your day</p>
          </div>
        </section>
        <div className="flex items-center justify-center py-20 px-4">
          <div className="text-center max-w-md">
            <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-5" style={{ backgroundColor: `${primaryColor}15` }}>
              <MapPin size={28} style={{ color: primaryColor }} />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 font-playfair mb-3">Coming Soon</h2>
            <p className="text-gray-500 mb-8">We're preparing something special. Check back soon to explore everything {hotel?.hotelName} has to offer.</p>
            <button
              onClick={() => navigate('/rooms')}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-white transition-all hover:-translate-y-0.5"
              style={{ backgroundColor: primaryColor }}
            >
              Browse Rooms <ChevronRight size={16} />
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* ── HERO SECTION ─────────────────────────────────────────────────── */}
      <section
        className="relative flex items-center justify-center bg-cover bg-center bg-no-repeat overflow-hidden"
        style={{
          minHeight: '75vh',
          backgroundImage: hotel?.bannerUrl
            ? `url(${hotel.bannerUrl})`
            : `linear-gradient(135deg, #0f172a 0%, #1e293b 100%)`,
        }}
      >
        {/* Dark overlay — ensures text always readable over any image */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/70" />

        {/* Bottom fade into white */}
        <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-white to-transparent" />

        {/* Content */}
        <div className="container relative z-10 text-center pt-20 pb-16">
          {/* Hotel name badge */}
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-medium mb-6 bg-white/15 backdrop-blur-sm border border-white/20 text-white/90">
            <MapPin size={14} />
            {hotel?.hotelName}
          </div>

          {/* Page title */}
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-tight mb-4 font-playfair drop-shadow-lg">
            {content.pageTitle || 'Explore Us'}
          </h1>

          {/* Subtitle */}
          {content.subTitle ? (
            <p className="text-xl md:text-2xl text-white/80 font-light mb-6 max-w-2xl mx-auto drop-shadow">
              {content.subTitle}
            </p>
          ) : (
            <p className="text-xl md:text-2xl text-white/80 font-light mb-6 drop-shadow">
              Discover your day
            </p>
          )}

          {/* Divider line */}
          <div className="flex items-center justify-center gap-4 mb-6">
            <div className="h-px w-16 bg-white/30" />
            <div className="w-1.5 h-1.5 rounded-full bg-white/60" />
            <div className="h-px w-16 bg-white/30" />
          </div>

          {/* Intro text */}
          {content.introText && (
            <p className="text-white/70 text-base md:text-lg leading-relaxed max-w-2xl mx-auto">
              {content.introText}
            </p>
          )}

          {/* Scroll indicator */}
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 text-white/40">
            <span className="text-xs tracking-widest uppercase">Scroll</span>
            <div className="w-px h-8 bg-gradient-to-b from-white/40 to-transparent" />
          </div>
        </div>
      </section>

      {/* ── VIDEO SECTION ────────────────────────────────────────────────── */}
      {embedUrl && (
        <section className="py-16 md:py-20 bg-white">
          <div className="container">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-10">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 font-playfair mb-3">
                  See It For Yourself
                </h2>
                <div className="w-16 h-1 rounded-full mx-auto" style={{ backgroundColor: primaryColor }} />
              </div>

              <div className="relative rounded-2xl overflow-hidden shadow-2xl aspect-video group">
                {videoPlaying ? (
                  <iframe
                    ref={videoRef}
                    src={embedUrl}
                    title="Explore video"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="absolute inset-0 w-full h-full"
                  />
                ) : (
                  <>
                    {thumbUrl && (
                      <img src={thumbUrl} alt="Video thumbnail" className="w-full h-full object-cover" />
                    )}
                    <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                      <button
                        onClick={() => setVideoPlaying(true)}
                        className="w-20 h-20 md:w-24 md:h-24 rounded-full bg-white/95 hover:bg-white shadow-2xl flex items-center justify-center transition-all duration-300 hover:scale-110 group"
                        aria-label="Play video"
                      >
                        <Play
                          size={32}
                          className="ml-1.5"
                          style={{ fill: primaryColor, color: primaryColor }}
                        />
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ── ACTIVITIES SECTION ───────────────────────────────────────────── */}
      {content.activities?.length > 0 && (
        <section className="py-16 md:py-24 bg-gray-50">
          <div className="container">
            <div className="text-center mb-14">
              <p className="text-sm font-semibold tracking-widest uppercase mb-3" style={{ color: primaryColor }}>
                What's Around
              </p>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 font-playfair mb-4">
                Things to Experience
              </h2>
              <div className="w-16 h-1 rounded-full mx-auto" style={{ backgroundColor: primaryColor }} />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {content.activities.map((activity, idx) => (
                <div
                  key={activity.id}
                  className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-500 border border-gray-100 hover:-translate-y-1"
                >
                  {/* Image */}
                  <div className="relative overflow-hidden aspect-[4/3]">
                    {activity.imageUrl ? (
                      <img
                        src={activity.imageUrl}
                        alt={activity.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center" style={{ backgroundColor: `${primaryColor}10` }}>
                        <span className="text-5xl font-bold" style={{ color: `${primaryColor}40` }}>
                          {String(idx + 1).padStart(2, '0')}
                        </span>
                      </div>
                    )}
                    <div
                      className="absolute top-4 left-4 w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold shadow-md"
                      style={{ backgroundColor: primaryColor }}
                    >
                      {idx + 1}
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors" style={{ '--tw-text-opacity': 1 }}>
                      {activity.title}
                    </h3>
                    {activity.description && (
                      <p className="text-gray-500 text-sm leading-relaxed line-clamp-3">{activity.description}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── GALLERY SECTION ──────────────────────────────────────────────── */}
      {content.galleryImages?.length > 0 && (
        <section className="py-16 md:py-24 bg-white">
          <div className="container">
            <div className="text-center mb-14">
              <p className="text-sm font-semibold tracking-widest uppercase mb-3" style={{ color: primaryColor }}>
                Visual Stories
              </p>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 font-playfair mb-4">
                A Glimpse of Paradise
              </h2>
              <div className="w-16 h-1 rounded-full mx-auto" style={{ backgroundColor: primaryColor }} />
            </div>

            {/* Masonry-style grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 md:gap-3">
              {content.galleryImages.map((img, idx) => (
                <div
                  key={img.id}
                  className={`relative group overflow-hidden rounded-xl cursor-pointer ${
                    idx % 7 === 0 ? 'col-span-2 row-span-2' :
                    idx % 5 === 0 ? 'col-span-2' : ''
                  }`}
                  style={{ aspectRatio: idx % 7 === 0 ? '1/1' : '4/3' }}
                  onClick={() => setLightboxIndex(idx)}
                >
                  <img
                    src={img.url}
                    alt=""
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/25 transition-colors duration-300 flex items-center justify-center">
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 w-10 h-10 rounded-full bg-white/80 flex items-center justify-center">
                      <svg className="w-5 h-5 text-gray-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                      </svg>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── CTA SECTION ──────────────────────────────────────────────────── */}
      <section className="py-16 md:py-20" style={{ background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}cc 100%)` }}>
        <div className="container text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white font-playfair mb-4">
            Ready to Experience This?
          </h2>
          <p className="text-white/80 text-lg mb-8 max-w-xl mx-auto">
            Book your stay and make unforgettable memories at {hotel?.hotelName}.
          </p>
          <button
            onClick={() => navigate('/rooms')}
            className="inline-flex items-center gap-2 px-8 py-4 bg-white text-gray-900 font-semibold rounded-full hover:shadow-2xl hover:-translate-y-0.5 transition-all duration-300"
          >
            Book Your Stay
            <ChevronRight size={18} />
          </button>
        </div>
      </section>

      {/* Lightbox */}
      {lightboxIndex !== null && (
        <Lightbox
          images={content.galleryImages}
          index={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
        />
      )}
    </div>
  );
};

export default Explore;
